import { useEffect, useEffectEvent } from 'react';
import { ApolloClient } from '@apollo/client';
import { useAuth } from '../../contexts/auth-context';
import { useGlobalUIStore } from '../../stores/global-ui-store';
import { AmberNotification, SourceType, WorkerMessageType, type WorkerMessage } from '../../util';
import {
  GetGrainBinFanSettingsDocument,
  GetViewerDocument,
  type GetViewerQuery,
  type ViewerFragmentFragment,
} from '../../api/graphql/__generated';
import { updateApnsConnection } from '../../api/http';
import { initIos } from '../../api/ios';
import { setServiceWorkerViewer } from '@amber-ui/service-worker-client';

/**
 * Handles service worker and iOS bridge messages that previously went through
 * the (deleted) Redux saga workerWatcher. Mounted once at the app root.
 *
 * Handles:
 * - WorkerMessageType.logout — delegated to AuthContext (handled there already)
 * - WorkerMessageType.auth — cross-tab auth sync
 * - WorkerMessageType.notify — refetch fan settings on push notification
 * - WorkerMessageType.activated — set viewer on SW activation
 * - WorkerMessageType.ios_register — iOS APNS token registration
 * - WorkerMessageType.new_version — set hasNewVersion flag in Zustand
 */
export function ServiceWorkerMessageHandler({ apolloClient }: { apolloClient: ApolloClient }) {
  const { login, logout, user_id } = useAuth();

  // useEffectEvent ensures the handler always reads the latest user_id, login,
  // and logout — preventing stale closures when auth state changes between
  // service worker messages (e.g., cross-tab auth sync updates user_id).
  const onWorkerMessage = useEffectEvent((message: WorkerMessage) => {
    handleWorkerMessage(message, apolloClient, login, logout, user_id);
  });

  useEffect(() => {
    // iOS bridge messages
    initIos((message) => {
      onWorkerMessage(message as WorkerMessage);
    });

    if (!navigator.serviceWorker) return;

    const handleMessage = (event: MessageEvent) => {
      onWorkerMessage(event.data as WorkerMessage);
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [apolloClient]);

  return null;
}

function handleWorkerMessage(
  message: WorkerMessage,
  apolloClient: ApolloClient,
  login: (viewer: ViewerFragmentFragment) => void,
  logout: (propagate: boolean) => void,
  currentUserId: string | null
) {
  switch (message.message_type) {
    case WorkerMessageType.logout:
      // Already handled by AuthContext's own service worker listener
      break;

    case WorkerMessageType.auth: {
      const viewer = (message as { payload: { viewer: ViewerFragmentFragment } }).payload.viewer;
      if (currentUserId === viewer.user_id) {
        return;
      }
      if (currentUserId && currentUserId !== viewer.user_id) {
        logout(true);
      }
      apolloClient.writeQuery({
        query: GetViewerDocument,
        data: { __typename: 'Query' as const, viewer },
      });
      login(viewer);
      break;
    }

    case WorkerMessageType.notify: {
      const note = (message as { payload: AmberNotification }).payload;
      // Non-critical side effects — silent failure is acceptable.
      void handleNotification(apolloClient, note).catch(() => {});
      break;
    }

    case WorkerMessageType.activated: {
      try {
        const result = apolloClient.readQuery<GetViewerQuery>({
          query: GetViewerDocument,
        });
        if (result?.viewer) {
          void setServiceWorkerViewer(result.viewer, false).catch(() => {});
        }
      } catch {
        // pass
      }
      break;
    }

    case WorkerMessageType.ios_register: {
      if (currentUserId) {
        const deviceToken = (message as { payload: { device_token: string } }).payload.device_token;
        void updateApnsConnection({
          user_id: currentUserId,
          device_token: deviceToken,
        }).catch(() => {});
      }
      break;
    }

    case WorkerMessageType.new_version: {
      useGlobalUIStore.getState().setHasNewVersion(true);
      break;
    }
  }
}

async function handleNotification(apolloClient: ApolloClient, note: AmberNotification) {
  switch (note.source_type) {
    case SourceType.fc: {
      const { grain_bin_id } = note.params;
      await apolloClient.query({
        query: GetGrainBinFanSettingsDocument,
        variables: { grain_bin_id },
        errorPolicy: 'all',
        fetchPolicy: 'network-only',
      });
      break;
    }
  }
}
